import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import Dropdown from 'bootstrap/js/dist/dropdown';
import * as XLSX from 'xlsx';
import ReactApexChart from 'react-apexcharts';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import {
  getPaginatedAssets,
  createAsset,
  deleteAsset,
  updateAsset,
  getAssetBySerial,
} from '../api/assetAPI';
import { getAssetType } from '../api/assetTypeAPI';
import { useSetting } from '../utils/AppSettingsContext';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useTableFilters } from '../utils/useTableFilters';
import { useValidator, renderErrorDot } from '../utils/useValidator';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useHasPageAccess } from '../masterLayout/MasterLayoutBody';
import AssetTasks from './child/AssetTasks';
import AssetMeterReadings from './child/AssetMeterReadings';
import AssetWorkOrders from './child/AssetWorkOrders';
import AssetLifeCycleCostTab from './child/AssetLifeCycleCostTab';
import WorkOrderMaintenanceIntensity from './child/WorkOrderMaintenanceIntensity';
import { getTasksByAsset } from '../api/tasksAPI';
import { getAssetMeter, createAssetMeter, updateAssetMeter } from '../api/asset-meterAPI';
import { getMeters } from '../api/metersAPI';
import { createMeterReadings } from '../api/meter-readingsAPI';
import {
  getAssetFinancial,
  createAssetFinancial,
  updateAssetFinancial,
} from '../api/asset-financialAPI';
import { getPlanningType } from '../api/planning-typeAPI';
import { getCustomers } from '../api/customersAPI';
import { getProjects } from '../api/projectsAPI';
import { getPlantRateCategoriesByPlantRateId, getPlantRatesByProjectId, calculatePlantRateTable } from '../api/plant-ratesAPI';
import { fetchData } from '../utils/apiHelpers';
import { getGroupById } from '../api/groupAPI';
import { clearCache } from '../utils/lookupCache';
import { deleteUploadedFile } from '../api/uploadAPI';
import { getPaginatedWorkOrders } from '../api/work-orderAPI';
import { getPaginatedForms } from '../api/work-order-formsAPI';
import { getUsersById } from '../api/usersAPI';
import { showToastError } from '../utils/errorController';
import AssetDataTable from './AssetDataTable';
import DocumentDataTable from './DocumentDataTable';
import AssetFormsDataTable from './AssetFormsDataTable';
import FormViewModal from './FormViewModal';
import AddAssetWizard from './AddAssetWizard';
import AssetPanelView from './AssetPanelView';
import AssetBulkAddModal from './AssetBulkAddModal';
import AssetMapView from './AssetMapView';
// import Barcode from 'react-barcode';
// import QRCode from 'react-qr-code';
import { useModule } from '../utils/useModule';
import { calculateMeterEndOfLife, resolveMeterEndOfLifeValue } from '../utils/meterUtils';
import { getGoogleTileUrl } from '../api/google-mapsAPI';
import { getLastAssetLocation } from '../api/asset-locationAPI';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const parseNumber = (value) => {
  if (value === null || value === undefined || value === '') return Number.NaN;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

const roundToTwo = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

const MS_IN_YEAR = 1000 * 60 * 60 * 24 * 365.25;
const PRIMARY_CHART_COLOR = '#0d6efd';
const USAGE_DEPRECIATION_INTERVAL = 1000;

const ASSET_PAGE_SIZE = 25;
const AssetListLayer = ({ viewMode: rawViewMode = 'table' }) => {
  const viewMode = useMemo(() => {
    if (typeof rawViewMode !== 'string') {
      return 'table';
    }

    const normalised = rawViewMode.trim().toLowerCase();
    return ['panel', 'table', 'cards', 'map'].includes(normalised) ? normalised : 'table';
  }, [rawViewMode]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [headerSearchContainer, setHeaderSearchContainer] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [editingId, seteditingId] = useState(null);
  const [newAsset, setNewAsset] = useState({});
  const [formReadOnly, setFormReadOnly] = useState(false);
  const userProfile = useUserProfile();
  const canEditAsset = userProfile?.privileges?.includes('assets:update');
  const canDeleteAsset = userProfile?.privileges?.includes('assets:delete');

  const hasPageAccess = useHasPageAccess();

  const showExtraAssetFields = useSetting('show_extra_asset_fields');
  const showAssetCategory = useSetting('show_asset_category');
  const useFunctionalLocation = useSetting('use_functional_location');
  const defaultCostCentre = useSetting('default_cost_centre');
  // Convert to numeric id when possible to ensure matching with option values
  const defaultCostCentreId =
    defaultCostCentre !== null && defaultCostCentre !== undefined
      ? Number(defaultCostCentre) || null
      : null;
  const planthireEnabled = useModule('planthire');
  const lccEnabled = useModule('lcc');
  const rotablesEnabled = useModule('rotables');

  const listContainerRef = useRef(null);
  const tasksRef = useRef(null);

  const [assets, setAssets] = useState([]);
  const [assetPage, setAssetPage] = useState(1);
  const [assetHasMore, setAssetHasMore] = useState(true);
  const [assetTotalCount, setAssetTotalCount] = useState(0);
  const [assetLoading, setAssetLoading] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [assetLocationLoading, setAssetLocationLoading] = useState(false);
  const [assetLocationError, setAssetLocationError] = useState('');
  const [mapScopedAssets, setMapScopedAssets] = useState([]);
  const isInitialAssetLoading = assetLoading && assetPage === 1 && assets.length === 0;
  const isLoadingMoreAssets = assetLoading && assetPage > 1 && assets.length > 0;

  const [meteredAssetIds, setMeteredAssetIds] = useState(new Set());
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetTreeRefreshKey, setAssetTreeRefreshKey] = useState(0);

  const [assetMeters, setAssetMeters] = useState([]);
  const [meterList, setMeterList] = useState([]);
  const [showAddMeterModal, setShowAddMeterModal] = useState(false);
  const [newMeterId, setNewMeterId] = useState(null);
  const [newStartReading, setNewStartReading] = useState('');
  const [newUtilisation, setNewUtilisation] = useState('');
  const [newPrimaryMeter, setNewPrimaryMeter] = useState(false);
  const [showEditMeterModal, setShowEditMeterModal] = useState(false);
  const [editingMeter, setEditingMeter] = useState(null);
  const [primaryEndOfLife, setPrimaryEndOfLife] = useState('');
  const primaryMeter = useMemo(() => assetMeters.find((m) => m.primaryMeter), [assetMeters]);
  const primaryStartReading = useMemo(() => {
    const parsed = parseNumber(primaryMeter?.startReading);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [primaryMeter]);
  const [assetFinancialId, setAssetFinancialId] = useState(null);
  const [PlanningTypes, setPlanningTypes] = useState([]);
  const [workOrdersLoaded, setWorkOrdersLoaded] = useState(false);
  const [tasksLoaded, setTasksLoaded] = useState(false);
  const [formsLoaded, setFormsLoaded] = useState(false);
  const [formsLoading, setFormsLoading] = useState(false);
  const [assetForms, setAssetForms] = useState([]);
  const [rotablesTasks, setRotablesTasks] = useState([]);
  const [rotablesTasksLoaded, setRotablesTasksLoaded] = useState(false);
  const [rotablesTasksLoading, setRotablesTasksLoading] = useState(false);
  const [lifeCycleCostRequestKey, setLifeCycleCostRequestKey] = useState(0);
  const [viewFormId, setViewFormId] = useState(null);
  const [assetTypes, setAssetTypes] = useState([]);
  const [serialRequired, setSerialRequired] = useState(false);
  const [noDuplicateSerial, setNoDuplicateSerial] = useState(false);
  const [, setSelectedGroup] = useState(null);
  const [customerList, setCustomerList] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [projectPlantRates, setProjectPlantRates] = useState([]);
  const [plantRateCategories, setPlantRateCategories] = useState([]);
  const [plantRateTableValues, setPlantRateTableValues] = useState({});
  const googleTileUrl = useMemo(() => getGoogleTileUrl(), []);
  const [assetLocationMarkers, setAssetLocationMarkers] = useState([]);
  const availableMeters = useMemo(() => {
    const assigned = new Set(assetMeters.map((am) => am.meterId));
    return meterList.filter((m) => !assigned.has(m.id));
  }, [meterList, assetMeters]);

  const addMeterFormData = useMemo(
    () => ({ meterId: newMeterId, utilisation: newUtilisation }),
    [newMeterId, newUtilisation],
  );

  const addMeterValidationFields = useMemo(
    () => [
      [{ field: 'meterId', label: 'Meter', required: true }],
      [
        {
          field: 'utilisation',
          label: 'Utilisation per Year',
          type: 'number',
          required: true,
          mustBeGreaterThanZero: true,
        },
      ],
    ],
    [],
  );

  const {
    errors: addMeterErrors,
    validate: validateAddMeterForm,
    resetErrors: resetAddMeterErrors,
  } = useValidator(addMeterValidationFields, addMeterFormData);

  const financeFieldKeys = [
    'purchasePrice',
    'depnRate',
    'depnType',
    'leased',
    'financeCompany',
    'leaseType',
    'leaseInterestRate',
    'leaseStartDate',
    'leaseEndDate',
    'leasePayments',
    'endValue',
    'residualValue'
  ];

  useEffect(() => {
    fetchData(getPlanningType, {}, setPlanningTypes);
  }, []);

  useEffect(() => {
    fetchData(getAssetType, {}, setAssetTypes);
  }, []);

  useEffect(() => {
    if (!planthireEnabled) return;
    (async () => {
      try {
        const [customers, projectsResp] = await Promise.all([
          getCustomers().catch(() => []),
          getProjects().catch(() => []),
        ]);
        setCustomerList(Array.isArray(customers?.data) ? customers.data : Array.isArray(customers) ? customers : []);
        const projectItems = Array.isArray(projectsResp?.projects)
          ? projectsResp.projects
          : Array.isArray(projectsResp?.data)
            ? projectsResp.data
            : Array.isArray(projectsResp)
              ? projectsResp
              : [];
        setAllProjects(projectItems);
      } catch (err) {
        console.error('Error loading plant hire dropdown data', err);
      }
    })();
  }, [planthireEnabled]);

  const selectedCustomerId = useMemo(() => {
    if (newAsset.customerId !== undefined && newAsset.customerId !== null) {
      return Number(newAsset.customerId);
    }
    if (newAsset.customer?.id !== undefined && newAsset.customer?.id !== null) {
      return Number(newAsset.customer.id);
    }
    return null;
  }, [newAsset.customerId, newAsset.customer?.id]);

  useEffect(() => {
    if (!planthireEnabled) return;

    if (!selectedCustomerId) {
      setProjectList((prev) => (prev.length === 0 ? prev : []));
      setNewAsset((prev) => {
        if ((prev.projectId ?? null) === null && !prev.project) return prev;
        return { ...prev, projectId: null, project: null };
      });
      return;
    }

    const filteredProjects = allProjects.filter(
      (project) => project.customerId === selectedCustomerId,
    );
    setProjectList((prev) => {
      if (
        prev.length === filteredProjects.length &&
        prev.every((project, index) => project.id === filteredProjects[index]?.id)
      ) {
        return prev;
      }
      return filteredProjects;
    });
    setNewAsset((prev) => {
      const currentProjectId =
        prev.projectId ?? (prev.project?.id !== undefined ? prev.project.id : null);
      if (!currentProjectId) {
        return prev;
      }
      if (filteredProjects.some((project) => project.id === currentProjectId)) {
        return prev;
      }
      return { ...prev, projectId: null, project: null };
    });
  }, [allProjects, planthireEnabled, selectedCustomerId]);

  useEffect(() => {
    const loadPlantRates = async () => {
      const selectedProjectId = Number(newAsset.projectId ?? newAsset.project?.id);
      const selectedGroupId = Number(newAsset.groupId);
      if (!planthireEnabled || !newAsset.hireAssetSwitch || !selectedProjectId || !selectedGroupId) {
        setProjectPlantRates([]);
        return;
      }
      const rows = await getPlantRatesByProjectId(selectedProjectId);
      const filtered = (Array.isArray(rows) ? rows : []).filter((rate) => Number(rate.groupId) === selectedGroupId);
      setProjectPlantRates(filtered);
    };
    loadPlantRates().catch(() => setProjectPlantRates([]));
  }, [planthireEnabled, newAsset.hireAssetSwitch, newAsset.projectId, newAsset.project?.id, newAsset.groupId]);

  useEffect(() => {
    const loadPlantRateTable = async () => {
      const selectedPlantRateId = Number(newAsset.plantRateId);
      if (!selectedPlantRateId) {
        setPlantRateCategories([]);
        setPlantRateTableValues({});
        return;
      }
      const [categories, tableValues] = await Promise.all([
        getPlantRateCategoriesByPlantRateId(selectedPlantRateId),
        calculatePlantRateTable({ plantRateId: selectedPlantRateId }),
      ]);
      setPlantRateCategories(Array.isArray(categories) ? categories : []);
      setPlantRateTableValues(tableValues || {});
    };
    loadPlantRateTable().catch(() => {
      setPlantRateCategories([]);
      setPlantRateTableValues({});
    });
  }, [newAsset.plantRateId]);

  const selectedPlantRateTableRows = useMemo(() => {
    const includedCategories = (plantRateCategories || []).filter((category) => Number(category.include) === 1);
    const rows = includedCategories.map((category) => {
      const normalizedCode = String(category.code || '').trim().toUpperCase();
      return {
        label: category.name,
        value: Number(plantRateTableValues?.[normalizedCode] || 0),
        expense: category.expenseCode?.name || '',
      };
    });
    const total = rows.reduce((sum, row) => sum + Number(row.value || 0), 0);
    return { rows, total };
  }, [plantRateCategories, plantRateTableValues]);

  useEffect(() => {
    (async () => {
      try {
        const meters = await getAssetMeter();
        setMeteredAssetIds(new Set(meters.map((m) => m.assetId)));
      } catch (err) {
        console.error('Error loading asset meter ids', err);
      }
    })();
  }, []);

  useEffect(() => {
    const selected = assetTypes.find((t) => t.id === newAsset.AssetTypeId);
    if (selected) {
      setSerialRequired(selected.mandatorySerialNumber === 1);
      setNoDuplicateSerial(selected.noDuplicateSerialNumbers === 1);
    } else {
      setSerialRequired(false);
      setNoDuplicateSerial(false);
    }
  }, [assetTypes, newAsset.AssetTypeId]);

  const formatStatusOption = (option) => (
    <div className="d-flex align-items-center gap-2">
      <span
        className={`d-inline-block rounded-circle bg-${option.colour || 'secondary'}`}
        style={{ width: '10px', height: '10px' }}
      ></span>
      <span>{option.label}</span>
    </div>
  );

  const getSelectValue = (value) => (value === undefined || value === null ? '' : String(value));

  const handleCustomerSelect = (value) => {
    const id = value ? Number(value) : null;
    const selectedCustomer = customerList.find((c) => c.id === id) || null;
    setNewAsset((prev) => ({
      ...prev,
      customerId: id,
      customer: selectedCustomer,
      projectId: null,
      project: null,
    }));
  };

  const handleProjectSelect = (value) => {
    const id = value ? Number(value) : null;
    const selectedProject = allProjects.find((p) => p.id === id) || null;
    setNewAsset((prev) => ({
      ...prev,
      projectId: id,
      project: selectedProject,
    }));
  };

  const resetAssetPagination = useCallback(() => {
    setAssets([]);
    setAssetPage(1);
    setAssetHasMore(true);
    setAssetTotalCount(0);
    setSelectedAsset(null);
  }, []);

  const handleSortChange = useCallback(
    (field) => {
      if (listContainerRef.current) {
        listContainerRef.current.scrollTop = 0;
      }
      resetAssetPagination();
      if (sortField === field) {
        if (sortOrder === 'asc') {
          setSortOrder('desc');
        } else if (sortOrder === 'desc') {
          setSortField(null);
          setSortOrder(null);
        } else {
          setSortOrder('asc');
        }
      } else {
        setSortField(field);
        setSortOrder('asc');
      }
    },
    [sortField, sortOrder, resetAssetPagination],
  );

  const getTypeColour = (type) => {
    if (!type) return 'secondary';
    const t = type.toLowerCase();
    if (t === 'advanced' || t === 'standard') return 'primary';
    if (t === 'basic') return 'secondary';
    return 'secondary';
  };

  const isBasicType = useMemo(() => {
    const desc = newAsset?.PlanningType?.typeDescription ||
      PlanningTypes.find((t) => t.id === newAsset.PlanningTypeId)?.typeDescription;
    return desc?.toLowerCase() === 'basic';
  }, [PlanningTypes, newAsset.PlanningTypeId, newAsset.PlanningType]);

  const isAdvancedType = useMemo(() => {
    const desc = newAsset?.PlanningType?.typeDescription ||
      PlanningTypes.find((t) => t.id === newAsset.PlanningTypeId)?.typeDescription;
    return desc?.toLowerCase() === 'advanced';
  }, [PlanningTypes, newAsset.PlanningTypeId, newAsset.PlanningType]);

  const planningTypeName = useMemo(
    () =>
      newAsset?.PlanningType?.typeDescription ||
      PlanningTypes.find((t) => t.id === newAsset.PlanningTypeId)?.typeDescription,
    [PlanningTypes, newAsset.PlanningTypeId, newAsset.PlanningType],
  );

  const assetTypeName = useMemo(
    () =>
      newAsset?.AssetType?.name ||
      assetTypes.find((t) => t.id === newAsset.AssetTypeId)?.name,
    [assetTypes, newAsset.AssetTypeId, newAsset.AssetType],
  );

  useEffect(() => {
    if (!planthireEnabled) return;

    setNewAsset((prev) => {
      const hireAssetSwitch = prev.hireStatus !== undefined && prev.hireStatus !== -1 ? 1 : 0;
      const hireStatus = hireAssetSwitch ? (prev.hireStatus === 1 ? 1 : 0) : -1;

      if (prev.hireAssetSwitch === hireAssetSwitch && prev.hireStatus === hireStatus) {
        return prev;
      }

      return { ...prev, hireAssetSwitch, hireStatus };
    });
  }, [planthireEnabled, newAsset.hireStatus, newAsset.hireAssetSwitch]);


  // Specify the input fields for the add / edit form
  // Uses the useFromBuilder hook to generate the form fields for the edit modal
  const assetGeneralFields = useMemo(
    () => [
      { field: 'assetDescription', label: 'Description', type: 'text', size: planthireEnabled ? 8 : 9, required: true },
      ...(planthireEnabled
        ? [{
          field: 'hireAssetSwitch',
          label: 'Hire Asset',
          type: 'switch',
          size: 1,
          disabled: newAsset.hireStatus === 1,
        }]
        : []),
      {
        field: "plantStatusId",
        label: "Asset Status",
        type: "select",
        size: 3,
        api: "plant-status",
        displayField: 'status',
        required: true,
        disabled: newAsset.assetDisposed,
        formatOptionLabel: formatStatusOption,
      },
      {
        field: 'groupId',
        label: 'Model or Group',
        type: 'select',
        size: 4,
        displayField: (g) => `${g.manufacturer?.manufacturer} ${g.groupName} ${g.class?.className}`,
        api: 'groups',
        required: true,
        isClearable: true,
      },
      { field: "assetNumber", label: "Asset Number", type: "text", size: 2, api: "", required: true },

      { field: 'imageBlock1', label: 'Image', type: 'emptyBorder', size: 6, required: false },
      { field: 'assetSerial', label: 'Serial Number', type: 'text', size: 3, required: serialRequired },
      { field: "costCenterId", label: "Cost Centre", type: "select", size: 3, api: "cost-centres", displayField: 'costCenterDescription', required: true },
      { field: 'generalSpacer1', label: '', type: 'empty', size: 6, required: false },
      { field: "assetDepartmentId", label: "Department", type: "select", size: 4, api: "hierarchy-structures", displayField: 'name', required: true, isClearable: true },
      // { field: 'generalSpacer8', label: '', type: 'empty', size: 6, required: false },
      { field: "assetCriticalityId", label: "Criticality", type: "select", size: 2, api: "asset-criticality", displayField: 'criticalityName', required: true },
      { field: 'generalSpacer2', label: '', type: 'empty', size: 2, required: false },
      { field: 'generalSpacer3', label: '', type: 'empty', size: 6, required: false },
      // { field: 'parentId', label: 'Parent Asset', type: 'select', size: '6', api: 'assets', required: false },
      { field: "assetStartDate", label: "Start Date", type: "date", size: 2, api: "", required: true },
      { field: "assetEndDate", label: "End Date", type: "date", size: 2, api: "", required: true },
      {
        field: "lifeInYears",
        label: "Life In Years",
        type: "select",
        size: 2,
        options: [
          { value: 1, label: "1 Year" },
          { value: 2, label: "2 Years" },
          { value: 3, label: "3 Years" },
          { value: 5, label: "5 Years" },
          { value: 8, label: "8 Years" },
          { value: 10, label: "10 Years" },
          { value: 15, label: "15 Years" },
          { value: 20, label: "20 Years" }
        ],
        required: false
      },
      { field: 'generalSpacer3a', label: '', type: 'empty', size: 6, required: false },

      { field: "assetImage", label: "Asset Image", type: "image", size: 2, api: "", required: false, directory: "modelimages", showImage: false },
      { field: 'buildYear', label: 'Build Year', type: 'text', size: 2, required: false },
      { field: "assetDisposed", label: "Asset Disposed?", type: "switch", size: 2, api: "", required: false },
       { field: 'generalSpacer3b', label: '', type: 'empty', size: 6, required: false },
      { field: 'warrantyMonths', label: 'Warranty Months', type: 'text', size: 2, required: false },
      { field: 'warrantyUsage', label: 'Warranty Usage', type: 'text', size: 2, required: false },
     
      // { field: 'warrantyBlock', label: '', type: 'empty', size: 12, required: false },
      
      { field: "assetFloc", label: "Functional Location", type: "text", size: 3, api: "", required: false },
      { field: "PlanningTypeId", label: "Planning Type", type: "select", size: 3, api: "planning-types", displayField: 'typeDescription', required: true },
      { field: 'notes', label: 'Notes', type: 'textarea', size: 12, required: false },
    ],
    [serialRequired, newAsset.assetDisposed, isAdvancedType, newAsset.plantStatus, newAsset.plantStatusId, planthireEnabled, newAsset.hireStatus],
  );

  const assetInfoFields = useMemo(() => [
    // { field: "generalSpacer4", label: "", type: "empty", size: 9, api: "", required: false },
    { field: "assetCode", label: "Asset Code", type: "text", size: 3, api: "", required: false },
    { field: "assetAltNumber", label: "Alternate Number", type: "text", size: 3, api: "", required: false },
    { field: "planningGroupId", label: "Planning Group", type: "select", size: 3, api: "planning-group", displayField: 'planningGroupName', required: false },
  ], []);

  const assetCategoryField = useMemo(() => [
    { field: "assetCategoryId", label: "Category", type: "select", size: 3, api: "categories", displayField: 'categoryName', required: false },
  ])

  const assetFinanceFields = useMemo(() => {
    const depreciationType = (newAsset.depnType || 'none').toLowerCase();
    const isDeclining = depreciationType === 'decliningbalance';
    const isUsageBased = depreciationType === 'usagebased';
    const endValueDisabled =
      depreciationType === 'none' || isDeclining || isUsageBased;
    const depnRateDisabled = !(isDeclining || isUsageBased);

    const fields = [
      { field: 'purchasePrice', label: 'Purchase Price', type: 'number', size: 3, required: false, step: 1 },
      {
        field: 'endValue',
        label: 'End of Life Value',
        type: 'number',
        size: 3,
        required: false,
        step: 0.01,
        disabled: endValueDisabled,
      },
      // { field: 'imageBlock1', label: 'Chart', type: 'emptyBorder', size: 6, required: false },
      {
        field: 'depnRate',
        label: 'Depreciation Rate %',
        type: 'number',
        size: 3,
        required: false,
        step: 0.01,
        disabled: depnRateDisabled,
      },

      {
        field: "depnType",
        label: "Depreciation Type",
        type: "select",
        size: 3,
        options: [
          { value: 'straightline', label: 'Straightline' },
          { value: 'decliningbalance', label: 'Declining Balance' },
          { value: 'usagebased', label: 'Usage Based' },
          { value: 'none', label: 'None' },
        ],
        required: false,
      },
      // { field: 'generalSpacer4', label: '', type: 'empty', size: 6, required: false },
      { field: "leased", label: "Leased?", type: "switch", size: 6, required: false },
      { field: 'generalSpacer5', label: '', type: 'empty', size: 6, required: false },
    ];

    if (newAsset.leased) {
      fields.push(
        { field: "financeCompany", label: "Finance Company", type: "text", size: 3, required: false },
        {
          field: "leaseType",
          label: "Lease Type",
          type: "select",
          size: 3,
          options: [
            { value: 'finance', label: 'Finance' },
            { value: 'operating', label: 'Operating' },
            { value: 'none', label: 'None' },
          ],
          required: false,
        },
        // { field: 'generalSpacer6', label: '', type: 'empty', size: 6, required: false },
        { field: "leaseInterestRate", label: "Lease Interest Rate %", type: "number", size: 3, required: false },
        { field: "leasePayments", label: "Lease Payments", type: "number", size: 3, required: false },
        // { field: 'generalSpacer7', label: '', type: 'empty', size: 6, required: false },
        { field: "leaseStartDate", label: "Lease Start Date", type: "date", size: 3, required: false },
        { field: "leaseEndDate", label: "Lease End Date", type: "date", size: 3, required: false },
        // { field: 'generalSpacer8', label: '', type: 'empty', size: 6, required: false },
        { field: 'residualValue', label: 'Residual Value', type: 'text', size: 3, required: false },
      );
    }

    return fields;
  }, [newAsset.leased, newAsset.depnType]);

  const assetDocumentFields = useMemo(
    () => [
      {
        field: 'assetFiles',
        label: 'Documents',
        type: 'files',
        size: 12,
        directory: 'assetfiles',
        accept:
          '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.bmp,.gif,.heic,.dwg,.dxf,.svg',
      },
    ],
    [],
  );

  const assetValidationFields = useMemo(
    () => [
      ...assetGeneralFields,
      ...assetInfoFields,
      ...assetFinanceFields,
      ...assetDocumentFields,
    ],
    [assetGeneralFields, assetInfoFields, assetFinanceFields, assetDocumentFields],
  );

  const {
    errors,
    setErrors,
    validate: validateAssetForm,
  } = useValidator(assetValidationFields, newAsset);

  const primaryMeterLifeValidationFields = useMemo(
    () => [[{ field: 'primaryEndOfLife', label: 'Useful Life', type: 'number' }]],
    [],
  );

  const primaryEndOfLifeFormData = useMemo(
    () => ({ primaryEndOfLife }),
    [primaryEndOfLife],
  );

  const {
    errors: primaryEndOfLifeErrors,
    setErrors: setPrimaryEndOfLifeErrors,
    validate: validatePrimaryEndOfLifeField,
    resetErrors: resetPrimaryEndOfLifeErrors,
  } = useValidator(primaryMeterLifeValidationFields, primaryEndOfLifeFormData);

  useEffect(() => {
    if (viewMode !== 'map') {
      if (mapScopedAssets.length) {
        setMapScopedAssets([]);
      }
      if (assetLocationMarkers.length) {
        setAssetLocationMarkers([]);
      }
      if (assetLocationLoading) {
        setAssetLocationLoading(false);
      }
      if (assetLocationError) {
        setAssetLocationError('');
      }
      return;
    }
    if (!mapScopedAssets.length) {
      setAssetLocationMarkers([]);
      return;
    }
    const assetIds = mapScopedAssets.map((asset) => asset.id).filter(Boolean);
    if (!assetIds.length) return;
    setAssetLocationLoading(true);
    setAssetLocationError('');
    getLastAssetLocation(assetIds)
      .then((data) => setAssetLocationMarkers(Array.isArray(data) ? data : []))
      .catch(() => {
        setAssetLocationMarkers([]);
        setAssetLocationError('Unable to load latest asset locations.');
      })
      .finally(() => setAssetLocationLoading(false));
  }, [viewMode, mapScopedAssets]);

  const tableFilters = useMemo(() => [

    { field: "classId", type: "dropDown", label: "Class", api: "class", displayField: "className", isClearable: "true", menuBarFilter: 1 },

    { field: "groupId", type: "dropDown", label: "Group", api: "groups", displayField: "groupName", isClearable: "true", menuBarFilter: 1 },
    { field: "plantStatusId", type: "dropDown", label: "Status", api: "plant-status", displayField: "status" },
    { field: "PlanningTypeId", type: "dropDown", label: "Planning Type", api: "planning-types", displayField: "typeDescription", menuBarFilter: viewMode === 'map' ? 0 : 1 },
    { field: "AssetTypeId", type: "dropDown", label: "Asset Type", api: "asset-type", displayField: "name" },
    { field: "assetDepartmentId", type: "dropDown", label: "Department", api: "hierarchy-structures", displayField: "name", menuBarFilter: viewMode === 'map' ? 1 : 0 },
    // { field: "assetCategoryId", type: "dropDown", label: "Category", api: "categories", displayField: "categoryName"  },
    // { field: "planningGroupId", type: "dropDown", label: "Planning Group", api: "planning-group", displayField: "planningGroupName"  },
    { field: "assetCriticalityId", type: "dropDown", label: "Criticality", api: "asset-criticality", displayField: "criticalityName" },
    { field: "costCenterId", type: "dropDown", label: "Cost Centre", api: "cost-centres", displayField: "costCenterDescription" },
    { field: "hireStatus", type: "switch", label: "Hire Status", api: "hire-status", displayField: "status" },
    { field: "assetDisposed", type: "switch", label: "Disposed" },
  ], [viewMode]);

  const { renderField: renderGeneralField } = useFormBuilder(assetGeneralFields, newAsset, setNewAsset, errors);
  const { renderField: renderInfoFields } = useFormBuilder(assetInfoFields, newAsset, setNewAsset, errors);
  const { renderField: renderCategoryField } = useFormBuilder(assetCategoryField, newAsset, setNewAsset, errors);
  const { renderField: renderFinanceFields } = useFormBuilder(assetFinanceFields, newAsset, setNewAsset, errors);
  const { renderField: renderDocumentFields } = useFormBuilder(
    assetDocumentFields,
    newAsset,
    setNewAsset,
    errors,
  );

  const depreciationChart = useMemo(() => {
    const formatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
    const formatValue = (value) => formatter.format(Number.isFinite(value) ? value : 0);

    const result = {
      hasData: false,
      message: '',
      options: undefined,
      series: [],
      computedEndValue: Number.NaN,
    };

    const purchasePrice = parseNumber(newAsset.purchasePrice);
    if (!Number.isFinite(purchasePrice)) {
      result.message = 'Enter a purchase price to view depreciation.';
      return result;
    }

    const depreciationType = (newAsset.depnType || 'none').toLowerCase();
    if (depreciationType === 'none') {
      result.message = 'Select a depreciation type to view depreciation.';
      return result;
    }

    if (!newAsset.assetStartDate || !newAsset.assetEndDate) {
      result.message = 'Select asset start and end dates to view depreciation.';
      return result;
    }

    const startDate = new Date(newAsset.assetStartDate);
    const endDate = new Date(newAsset.assetEndDate);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      result.message = 'Select asset start and end dates to view depreciation.';
      return result;
    }

    if (endDate <= startDate) {
      result.message = 'Asset end date must be after the start date.';
      return result;
    }

    const diffYears = (endDate.getTime() - startDate.getTime()) / MS_IN_YEAR;
    const totalYears = Math.max(1, Math.ceil(diffYears));
    const categories = Array.from({ length: totalYears + 1 }, (_, index) => `${startDate.getFullYear() + index
      }`);

    let data = [];

    if (depreciationType === 'straightline') {
      const endValue = parseNumber(newAsset.endValue);
      if (!Number.isFinite(endValue)) {
        result.message = 'Enter an end of life value to view depreciation.';
        return result;
      }
      const pointsCount = categories.length;
      const step = pointsCount > 1 ? (purchasePrice - endValue) / (pointsCount - 1) : 0;
      data = Array.from({ length: pointsCount }, (_, index) =>
        roundToTwo(purchasePrice - step * index),
      );
      data[data.length - 1] = roundToTwo(endValue);
      result.computedEndValue = roundToTwo(endValue);
    } else if (depreciationType === 'decliningbalance') {
      const rate = parseNumber(newAsset.depnRate);
      if (!Number.isFinite(rate)) {
        result.message = 'Enter a depreciation rate to view depreciation.';
        return result;
      }
      const pointsCount = categories.length;
      let current = purchasePrice;
      data = [];
      for (let index = 0; index < pointsCount; index += 1) {
        data.push(roundToTwo(Math.max(current, 0)));
        if (index < pointsCount - 1) {
          current = Math.max(0, current * (1 - rate / 100));
        }
      }
      result.computedEndValue = data[data.length - 1] ?? Number.NaN;
    } else if (depreciationType === 'usagebased') {
      const rate = parseNumber(newAsset.depnRate);
      if (!Number.isFinite(rate)) {
        result.message = 'Enter a depreciation rate to view depreciation.';
        return result;
      }

      const primaryMeter = assetMeters.find((m) => m.primaryMeter);
      if (!primaryMeter) {
        result.message = 'Assign a primary meter with utilisation to view depreciation.';
        return result;
      }

      const annualUtilisation = parseNumber(primaryMeter.utilisation);
      if (!Number.isFinite(annualUtilisation) || annualUtilisation <= 0) {
        result.message = 'Enter an annual utilisation for the primary meter to view depreciation.';
        return result;
      }

      const parsedEndOfLife = [primaryEndOfLife, primaryMeter.endOfLife]
        .map((val) => parseNumber(val))
        .find((val) => Number.isFinite(val) && val > 0);
      const adjustedEndOfLife = Number.isFinite(parsedEndOfLife)
        ? parsedEndOfLife - primaryStartReading
        : Number.NaN;
      const totalUsage = Number.isFinite(adjustedEndOfLife) && adjustedEndOfLife > 0
        ? adjustedEndOfLife
        : annualUtilisation * totalYears;

      let current = purchasePrice;
      let remainingUsage = Math.max(totalUsage, 0);
      data = [];
      for (let index = 0; index < categories.length; index += 1) {
        data.push(roundToTwo(Math.max(current, 0)));
        if (index < categories.length - 1 && remainingUsage > 0) {
          const actualUsage = Math.min(annualUtilisation, remainingUsage);
          let nextValue = current;
          const fullSteps = Math.floor(actualUsage / USAGE_DEPRECIATION_INTERVAL);
          for (let step = 0; step < fullSteps; step += 1) {
            nextValue *= 1 - rate / 100;
          }
          const remainder = actualUsage % USAGE_DEPRECIATION_INTERVAL;
          if (remainder > 0) {
            nextValue *= 1 - (rate / 100) * (remainder / USAGE_DEPRECIATION_INTERVAL);
          }
          current = Math.max(0, nextValue);
          remainingUsage -= actualUsage;
        }
      }
      result.computedEndValue = data[data.length - 1] ?? Number.NaN;
    } else {
      data = Array.from({ length: categories.length }, () => roundToTwo(purchasePrice));
      result.computedEndValue = roundToTwo(purchasePrice);
    }

    const options = {
      chart: {
        type: 'line',
        toolbar: { show: false },
        animations: { enabled: false },
        background: 'transparent',
      },
      stroke: { width: 3, curve: 'straight', colors: [PRIMARY_CHART_COLOR] },
      markers: { size: 4, colors: [PRIMARY_CHART_COLOR], strokeColors: '#ffffff', strokeWidth: 2 },
      dataLabels: { enabled: false },
      grid: { borderColor: '#e5e7eb', strokeDashArray: 4, padding: { left: 40, right: 12 } },
      xaxis: { categories, title: { text: 'Year' } },
      yaxis: { title: { text: 'Asset Value' }, labels: { formatter: formatValue, offsetX: 0 } },
      tooltip: { y: { formatter: formatValue } },
    };

    return {
      ...result,
      hasData: true,
      options,
      series: [{ name: 'Asset Value', data }],
    };
  }, [
    assetMeters,
    newAsset.assetEndDate,
    newAsset.assetStartDate,
    newAsset.depnRate,
    newAsset.depnType,
    newAsset.endValue,
    newAsset.purchasePrice,
    primaryStartReading,
    primaryEndOfLife,
  ]);

  useEffect(() => {
    const depreciationType = (newAsset.depnType || 'none').toLowerCase();
    if (!['decliningbalance', 'usagebased'].includes(depreciationType)) {
      return;
    }

    const computed = depreciationChart.computedEndValue;
    if (!Number.isFinite(computed)) {
      return;
    }

    const rounded = roundToTwo(computed);
    setNewAsset((prev) => {
      const prevType = (prev.depnType || 'none').toLowerCase();
      if (prevType !== depreciationType) {
        return { ...prev, endValue: rounded };
      }
      const previous = parseNumber(prev.endValue);
      if (Number.isFinite(previous) && Math.abs(previous - rounded) < 0.01) {
        return prev;
      }
      return { ...prev, endValue: rounded };
    });
  }, [depreciationChart.computedEndValue, newAsset.depnType]);

  const tabHasErrors = (fields) => fields.some((f) => errors[f.field]);

  const generalTabHasError = tabHasErrors([...assetGeneralFields, ...assetInfoFields]);
  const financeTabHasError = tabHasErrors(assetFinanceFields);
  const documentsTabHasError = tabHasErrors(assetDocumentFields);

  const {
    appliedFilters,
    appliedFilterParams,
    renderFilterControls
  } = useTableFilters(tableFilters, { assetDisposed: false });

  useEffect(() => {
    setHeaderSearchContainer(document.getElementById('header-search-container'));
  }, []);

  const updateAssets = async () => {
    resetAssetPagination();
    setAssetTreeRefreshKey((key) => key + 1);
    const btn = document.getElementById('apply-filters-btn');
    if (btn) btn.click();
  };

  const fetchAssetsForDepartment = useCallback(
    async (departmentId, page = 1, pageSize = 25) => {
      const filters = appliedFilterParams || {};
      const { assetDepartmentId, ...otherFilters } = filters;

      const numericDeptId = Number(departmentId);
      const resolvedDepartmentId = Number.isNaN(numericDeptId) ? departmentId : numericDeptId;

      const allowDepartment = (() => {
        if (
          assetDepartmentId === undefined ||
          assetDepartmentId === null ||
          assetDepartmentId === '' ||
          (Array.isArray(assetDepartmentId) && assetDepartmentId.length === 0)
        ) {
          return true;
        }

        if (Array.isArray(assetDepartmentId)) {
          return assetDepartmentId
            .map((id) => Number(id))
            .includes(Number(resolvedDepartmentId));
        }

        return Number(assetDepartmentId) === Number(resolvedDepartmentId);
      })();

      if (!allowDepartment) {
        return { data: [], total: 0 };
      }

      const params = {
        ...otherFilters,
        page,
        pageSize,
        search: searchTerm,
        assetDepartmentId: resolvedDepartmentId,
        sortField: sortField || undefined,
        sortOrder: sortOrder || undefined,
      };

      return getPaginatedAssets(params);
    },
    [appliedFilterParams, searchTerm, sortField, sortOrder],
  );

  const loadAssetMeters = async (id) => {
    if (!id) return;
    try {
      const [am, meters] = await Promise.all([getAssetMeter(), getMeters()]);
      setAssetMeters(am.filter((m) => m.assetId === id));
      setMeterList(meters);
    } catch (err) {
      console.error('Error loading meters', err);
    }
  };

  const loadAssetFinancial = async (id) => {
    if (!id) return;
    if (!userProfile?.privileges?.includes('asset-financial:view')) return;
    try {
      const financials = await getAssetFinancial();
      const record = financials.find((f) => f.assetId === id);
      if (record) {
        setAssetFinancialId(record.id);
        const formatted = {
          ...record,
          purchasePrice:
            record.purchasePrice !== undefined && record.purchasePrice !== null
              ? String(Math.round(record.purchasePrice))
              : record.purchasePrice,
        };
        setNewAsset((prev) => ({ ...prev, ...formatted }));
      } else {
        setAssetFinancialId(null);
      }
    } catch (err) {
      console.error('Error loading asset financial', err);
    }
  };

  const loadAssetForms = async (id) => {
    if (!id) return;
    try {
      setFormsLoading(true);
      const resp = await getPaginatedForms({ assetId: id, completed: 1, pageSize: 100 });
      const data = resp.data || resp;
      const userIds = [...new Set(data.map((f) => f.createdBy).filter(Boolean))];
      const userMap = {};
      await Promise.all(
        userIds.map((uid) =>
          getUsersById(uid)
            .then((u) => { userMap[uid] = u; })
            .catch(() => { })
        )
      );
      const withUsers = data.map((f) => ({
        ...f,
        createdByUser: f.createdByUser || userMap[f.createdBy] || null,
      }));
      setAssetForms(withUsers);
    } catch (err) {
      console.error('Error loading asset forms', err);
    } finally {
      setFormsLoading(false);
    }
  };

  const loadRotablesTasks = useCallback(
    async (id) => {
      if (!id) {
        setRotablesTasks([]);
        return;
      }
      setRotablesTasksLoading(true);
      try {
        const fetched = await getTasksByAsset(id, { rotablesOnly: true });
        const items = Array.isArray(fetched) ? fetched : [];
        setRotablesTasks(items);
      } catch (err) {
        console.error('Error loading rotable tasks', err);
        showToastError('Unable to load rotable tasks for this asset.');
        setRotablesTasks([]);
      } finally {
        setRotablesTasksLoading(false);
      }
    },
    [showToastError],
  );

  const rotableTasks = useMemo(
    () =>
      rotablesTasks.filter((task) => {
        const rotable = task?.rotables || task?.rotable;
        return Boolean(task?.rotablesId || rotable?.id);
      }),
    [rotablesTasks],
  );

  useEffect(() => {
    resetAssetPagination();
  }, [appliedFilters, searchTerm, resetAssetPagination]);

  useEffect(() => {
    if (viewMode !== 'panel') {
      setSelectedAsset(null);
      return;
    }
    setSelectedAsset((prev) => {
      if (!assets.length) {
        return null;
      }
      if (prev && assets.some((asset) => asset.id === prev.id)) {
        return prev;
      }
      return assets[0];
    });
  }, [viewMode, assets]);

  useEffect(() => {
    if (editingId) {
      loadAssetMeters(editingId);
      loadAssetFinancial(editingId);
    } else {
      setAssetMeters([]);
      setAssetFinancialId(null);
    }
    setWorkOrdersLoaded(false);
    setTasksLoaded(false);
    setFormsLoaded(false);
    setAssetForms([]);
    setViewFormId(null);
    setRotablesTasksLoaded(false);
    setRotablesTasks([]);
  }, [editingId]);

  // When adding a new asset, ensure cost centre defaults once setting loads
  useEffect(() => {
    if (
      showModal &&
      !editingId &&
      defaultCostCentreId &&
      !newAsset.costCenterId
    ) {
      setNewAsset((prev) => ({ ...prev, costCenterId: defaultCostCentreId }));
    }
  }, [showModal, editingId, defaultCostCentreId, newAsset.costCenterId]);

  useEffect(() => {
    if (!primaryMeter) {
      setPrimaryEndOfLife('');
      resetPrimaryEndOfLifeErrors();
      return;
    }

    const calculated = calculateMeterEndOfLife(newAsset, primaryMeter.utilisation);
    if (calculated != null) {
      const calculatedWithStartReading = Math.round(calculated + primaryStartReading);
      if (Math.abs(calculatedWithStartReading - Number(primaryEndOfLife)) > 1) {
        setPrimaryEndOfLife(String(calculatedWithStartReading));
      }
      return;
    }

    if (primaryMeter.endOfLife !== undefined && primaryMeter.endOfLife !== null) {
      const rounded = Math.round(primaryMeter.endOfLife);
      if (Math.abs(rounded - Number(primaryEndOfLife)) > 1) {
        setPrimaryEndOfLife(String(rounded));
      }
    }
  }, [
    primaryMeter,
    primaryStartReading,
    newAsset.assetStartDate,
    newAsset.assetEndDate,
    newAsset.lifeInYears,
    resetPrimaryEndOfLifeErrors,
  ]);

  // When adding a new asset, prefill image from selected group
  useEffect(() => {
    if (!newAsset.groupId || editingId) return;
    (async () => {
      try {
        const grp = await getGroupById(newAsset.groupId);
        if (grp?.groupImage) {
          setNewAsset((prev) => ({ ...prev, assetImage: grp.groupImage }));
        }
      } catch (err) {
        console.error('Error loading group image', err);
      }
    })();
  }, [newAsset.groupId, editingId]);

  useEffect(() => {
    if (!newAsset.groupId) {
      setSelectedGroup(null);
      return;
    }
    (async () => {
      try {
        const grp = await getGroupById(newAsset.groupId);
        setSelectedGroup({
          value: grp.id,
          label: `${grp.manufacturer?.manufacturer} ${grp.groupName} ${grp.class?.className}`,
        });
      } catch (err) {
        console.error('Error setting selected group', err);
      }
    })();
  }, [newAsset.groupId]);

  useEffect(() => {
    if (newAsset.assetDisposed && newAsset.plantStatusId !== 1) {
      // Ensure disposed assets always use the disposed plant status
      setNewAsset((prev) => ({ ...prev, plantStatusId: 1 }));
    } else if (!newAsset.assetDisposed && newAsset.plantStatusId === 1) {
      // Switching from disposed to not disposed should reset status to Operational
      setNewAsset((prev) => ({ ...prev, plantStatusId: 6 }));
    }
  }, [newAsset.assetDisposed, newAsset.plantStatusId]);

  const validatePrimaryEndOfLifeValue = useCallback(
    (value) => {
      const baseValid = validatePrimaryEndOfLifeField();
      const parsedValue = parseNumber(value);
      const startReading = primaryStartReading;
      let error = '';

      if (value === '' || value === null) {
        setPrimaryEndOfLifeErrors((prev) => {
          if (!prev.primaryEndOfLife) return prev;
          const next = { ...prev };
          delete next.primaryEndOfLife;
          return next;
        });
        return baseValid;
      }

      if (!Number.isFinite(parsedValue)) {
        error = 'Useful Life must be a valid number';
      } else if (parsedValue - startReading <= 0) {
        error = `Useful Life must be greater than Start Reading of ${startReading}`;
      }

      setPrimaryEndOfLifeErrors((prev) => {
        const next = { ...prev };
        if (error) {
          next.primaryEndOfLife = error;
        } else {
          delete next.primaryEndOfLife;
        }
        return next;
      });

      return baseValid && !error;
    },
    [primaryStartReading, setPrimaryEndOfLifeErrors, validatePrimaryEndOfLifeField],
  );

  useEffect(() => {
    if (primaryEndOfLife === '') {
      resetPrimaryEndOfLifeErrors();
      return;
    }

    validatePrimaryEndOfLifeValue(primaryEndOfLife);
  }, [primaryEndOfLife, primaryStartReading, resetPrimaryEndOfLifeErrors, validatePrimaryEndOfLifeValue]);

  // Auto calculate end date based on selected life in years
  useEffect(() => {
    if (!newAsset.assetStartDate || !newAsset.lifeInYears) return;
    const start = new Date(newAsset.assetStartDate);
    if (isNaN(start.getTime())) return;
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + Number(newAsset.lifeInYears));
    const iso = end.toISOString();
    if (newAsset.assetEndDate !== iso) {
      setNewAsset((prev) => ({ ...prev, assetEndDate: iso }));
    }
  }, [newAsset.assetStartDate, newAsset.lifeInYears]);

  useEffect(() => {
    const fetchAssets = async () => {
      setAssetLoading(true);
      try {
        const resp = await getPaginatedAssets({
          page: assetPage,
          pageSize: ASSET_PAGE_SIZE,
          search: searchTerm,
          sortField: sortField || undefined,
          sortOrder: sortOrder || undefined,
          ...appliedFilterParams,
        });
        let updatedAssets = [];
        setAssets((prev) => {
          const existingIds = new Set(prev.map((a) => a.id));
          const newAssets = resp.data.filter((a) => !existingIds.has(a.id));
          updatedAssets = [...prev, ...newAssets];
          return updatedAssets;
        });
        const total = Number(resp?.total) || 0;
        setAssetTotalCount(total);
        setAssetHasMore(updatedAssets.length < total);
      } catch (err) {
        console.error('Error fetching assets:', err);
      } finally {
        setAssetLoading(false);
      }
    };

    fetchAssets();
  }, [assetPage, appliedFilterParams, searchTerm, sortField, sortOrder]);

  useEffect(() => {
    if (viewMode !== 'map') {
      return;
    }

    let cancelled = false;

    const fetchMapScopedAssets = async () => {
      setAssetLocationLoading(true);
      setAssetLocationError('');
      try {
        const pageSize = 250;
        const loadedAssets = [];
        let page = 1;
        let total = 0;

        // Map mode does not render the scroll container, so fetch all pages explicitly.
        do {
          const resp = await getPaginatedAssets({
            page,
            pageSize,
            search: searchTerm,
            sortField: sortField || undefined,
            sortOrder: sortOrder || undefined,
            ...appliedFilterParams,
          });
          const rows = Array.isArray(resp?.data) ? resp.data : [];
          total = Number(resp?.total) || 0;
          loadedAssets.push(...rows);
          page += 1;
        } while (loadedAssets.length < total);

        if (cancelled) return;
        const dedupedAssets = Array.from(
          new Map(loadedAssets.map((asset) => [asset.id, asset])).values(),
        );
        setMapScopedAssets(dedupedAssets);
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching full asset scope for map mode:', error);
        setMapScopedAssets([]);
        setAssetLocationMarkers([]);
        setAssetLocationError('Unable to load assets for map mode.');
      } finally {
        if (!cancelled) {
          setAssetLocationLoading(false);
        }
      }
    };

    fetchMapScopedAssets();

    return () => {
      cancelled = true;
    };
  }, [viewMode, appliedFilterParams, searchTerm, sortField, sortOrder]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container || viewMode === 'map' || viewMode === 'panel') return;

    const onScroll = () => {
      if (
        container.scrollTop + container.clientHeight >= container.scrollHeight - 120 &&
        assetHasMore &&
        assets.length < assetTotalCount &&
        !assetLoading
      ) {
        setAssetPage((p) => p + 1);
      }
    };

    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [assetHasMore, assetLoading, viewMode, assets.length, assetTotalCount]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container || viewMode === 'map' || viewMode === 'panel') return;
    if (assetLoading || !assetHasMore) return;
    if (assets.length >= assetTotalCount && assetTotalCount > 0) return;

    // If current rows don't overflow yet, keep loading until scroll can continue.
    if (container.scrollHeight <= container.clientHeight + 8) {
      setAssetPage((p) => p + 1);
    }
  }, [assets, assetHasMore, assetLoading, viewMode, assetTotalCount]);

  useEffect(() => {
    const div = document.getElementById('imageBlock1');
    if (!div) return;

    if (showModal && editingId && newAsset.assetImage) {
      div.style.backgroundImage = `url(${process.env.REACT_APP_SPACES_BASE}/${newAsset.assetImage})`;
      div.style.backgroundSize = 'cover';
      div.style.backgroundPosition = 'center';
    } else {
      div.style.backgroundImage = '';
    }
  }, [showModal, editingId, newAsset.assetImage]);

  // Handle form inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedAsset = {
      ...newAsset,
      [name]: value === '' ? null : isNaN(value) ? value : Number(value),
    };
    setNewAsset(updatedAsset);
  };

  const generateCsv = () => {
    const headers = [
      'Asset',
      'Status',
      'Group',
      ...(useFunctionalLocation ? ['FLOC'] : []),
      'Serial',
      'Type',
      'Department',
    ];

    const rows = assets.map((row) => [
      `${row.assetNumber} | ${row.assetDescription}`,
      row.plantStatus?.status || '',
      row.group?.groupName || '',
      ...(useFunctionalLocation ? [row.assetFloc || ''] : []),
      row.assetSerial || '',
      row.PlanningType?.typeDescription || '',
      row.assetDepartment?.name || '',
    ]);

    const csvLines = [
      headers.join(','),
      ...rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')),
    ];
    return csvLines.join('\n');
  };

  const handleExportExcel = () => {
    const headers = [
      'Asset',
      'Status',
      'Group',
      ...(useFunctionalLocation ? ['FLOC'] : []),
      'Serial',
      'Type',
      'Department',
    ];

    const rows = assets.map((row) => [
      `${row.assetNumber} | ${row.assetDescription}`,
      row.plantStatus?.status || '',
      row.group?.groupName || '',
      ...(useFunctionalLocation ? [row.assetFloc || ''] : []),
      row.assetSerial || '',
      row.PlanningType?.typeDescription || '',
      row.assetDepartment?.name || '',
    ]);

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Assets');
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'assets.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const csv = generateCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'assets.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleView = (asset) => {
    setFormReadOnly(true);
    setNewAsset({});
    seteditingId(null);

    setTimeout(() => {
      const converted = {
        ...asset,
        customerId: asset.customerId ?? asset.customer?.id ?? null,
        projectId: asset.projectId ?? asset.project?.id ?? null,
        assetFiles: Array.isArray(asset.assetFiles)
          ? asset.assetFiles.map((f) =>
            typeof f === 'string' ? { name: f, filename: f } : f,
          )
          : [],
      };
      setNewAsset(converted);
      seteditingId(asset.id);
      setShowModal(true);
    }, 0); // Allows state reset before modal re-renders
  };

  const handleEdit = (asset) => {
    seteditingId(null);
    const converted = {
      ...asset,
      customerId: asset.customerId ?? asset.customer?.id ?? null,
      projectId: asset.projectId ?? asset.project?.id ?? null,
      assetFiles: Array.isArray(asset.assetFiles)
        ? asset.assetFiles.map((f) =>
          typeof f === 'string' ? { name: f, filename: f } : f,
        )
        : [],
    };
    setNewAsset(converted);
    seteditingId(asset.id);
    setFormReadOnly(false);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete asset?')) return false;

    try {
      const resp = await getPaginatedWorkOrders({ page: 1, pageSize: 1, assetId: id });
      if (resp.total > 0) {
        showToastError('Cannot delete the asset, it has linked work orders');
        return false;
      }
    } catch (err) {
      console.error('Error checking asset work orders:', err);
      showToastError('Cannot delete the asset, it has linked work orders');
      return false;
    }

    await deleteAsset(id);
    setAssets((prev) => prev.filter((a) => a.id !== id));
    setSelectedAsset((prev) => (prev?.id === id ? null : prev));
    setAssetTreeRefreshKey((key) => key + 1);

    return true;
  };

  const handleFileDelete = async (filename, idx) => {
    if (!window.confirm('Delete this file?')) return;
    setNewAsset((prev) => ({
      ...prev,
      assetFiles: prev.assetFiles.filter((_, i) => i !== idx),
    }));
    try {
      await deleteUploadedFile(filename);
    } catch (err) {
      console.error('Error deleting file', err);
    }
  };

  const handleSaveMeter = async () => {
    if (!editingId) return;
    if (!validateAddMeterForm()) return;
    try {
      await createAssetMeter({
        assetId: editingId,
        meterId: newMeterId,
        startReading: newStartReading || 0,
        utilisation: Number(newUtilisation) || 0,
        primaryMeter: newPrimaryMeter ? 1 : 0,
      });
      if (newAsset.assetStartDate) {
        await createMeterReadings({
          assetId: editingId,
          metersId: newMeterId,
          value: parseFloat(newStartReading) || 0,
          readingDate: new Date(newAsset.assetStartDate).toISOString(),
        });
      }
      setShowAddMeterModal(false);
      setNewMeterId(null);
      setNewStartReading('');
      setNewUtilisation('');
      setNewPrimaryMeter(false);
      resetAddMeterErrors();
      await loadAssetMeters(editingId);
    } catch (err) {
      console.error('Error adding meter', err);
    }
  };

  const handleEditMeter = (meter) => {
    setEditingMeter({
      ...meter,
      startReading:
        meter.startReading !== undefined && meter.startReading !== null
          ? String(Math.round(meter.startReading))
          : meter.startReading,
    });
    setShowEditMeterModal(true);
  };

  const handleUpdateMeter = async () => {
    if (!editingMeter) return;
    const otherPrimary = assetMeters.find(
      (am) => am.id !== editingMeter.id && am.primaryMeter,
    );
    if (!editingMeter.primaryMeter && !otherPrimary) {
      alert('One meter must be primary');
      return;
    }
    try {
      if (editingMeter.primaryMeter) {
        const updates = assetMeters
          .filter((am) => am.id !== editingMeter.id && am.primaryMeter)
          .map((am) => updateAssetMeter(am.id, { primaryMeter: 0 }));
        if (updates.length > 0) await Promise.all(updates);
      }
      await updateAssetMeter(editingMeter.id, {
        startReading: editingMeter.startReading || 0,
        utilisation: editingMeter.utilisation || 0,
        primaryMeter: editingMeter.primaryMeter ? 1 : 0,
      });
      setShowEditMeterModal(false);
      setEditingMeter(null);
      await loadAssetMeters(editingId);
    } catch (err) {
      console.error('Error updating meter', err);
    }
  };

  const clearForm = () => {
    setNewAsset({});
    seteditingId(null);
    setErrors({});
    setAssetMeters([]);
    setMeterList([]);
    setAssetFinancialId(null);
    setNewMeterId(null);
    setNewStartReading('');
    setNewUtilisation('');
    setNewPrimaryMeter(false);
    setShowAddMeterModal(false);
    setShowEditMeterModal(false);
    setEditingMeter(null);
  };

  const closeModal = () => {
    setShowModal(false);
    // document.body.classList.remove('modal-open');
    // const backdrop = document.querySelector('.modal-backdrop');
    // if (backdrop) backdrop.remove();
    clearForm();
  }

  const handleSave = async () => {
    const isValid = validateAssetForm();
    const isPrimaryLifeValid = validatePrimaryEndOfLifeValue(primaryEndOfLife);

    const warrantyErrors = {};
    if (!(newAsset.dateBasedWarranty ?? 1) && !newAsset.warrantyMeterId) {
      warrantyErrors.warrantyMeterId = 'Warranty Meter is required';
    }
    if (Object.keys(warrantyErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...warrantyErrors }));
    }

    if (!isValid || !isPrimaryLifeValid || Object.keys(warrantyErrors).length > 0) return;
    try {
      // strip out any null or object values from newAsset response
      const cleanAssetPayload = {};
      const financePayload = {};
      for (const key in newAsset) {
        if (key === 'hireAssetSwitch') continue;
        if (newAsset[key] !== undefined && newAsset[key] !== '' && typeof newAsset[key] !== 'object') {
          if (financeFieldKeys.includes(key)) {
            financePayload[key] = newAsset[key];
          } else {
            cleanAssetPayload[key] = newAsset[key];
          }
        }
      }

      ['customerId', 'projectId', 'plantRateId'].forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(newAsset, key) && newAsset[key] === null) {
          cleanAssetPayload[key] = null;
        }
      });

      const toDateOnly = (val) => {
        if (!val) return null;

        // If it's already a YYYY-MM-DD string, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;

        // Parse using local date components to avoid timezone shift
        const d = new Date(val);
        if (isNaN(d.getTime())) return String(val).slice(0, 10);

        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      };

      if (cleanAssetPayload.assetStartDate)
        cleanAssetPayload.assetStartDate = toDateOnly(cleanAssetPayload.assetStartDate);
      if (cleanAssetPayload.assetEndDate) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(cleanAssetPayload.assetEndDate)) {
          cleanAssetPayload.assetEndDate = new Date(
            `${cleanAssetPayload.assetEndDate}T00:00:00Z`,
          ).toISOString();
        }
      }
      if (cleanAssetPayload.assetDisposalDate)
        cleanAssetPayload.assetDisposalDate = toDateOnly(cleanAssetPayload.assetDisposalDate);
      if (cleanAssetPayload.leaseStartDate)
        cleanAssetPayload.leaseStartDate = toDateOnly(cleanAssetPayload.leaseStartDate);
      if (cleanAssetPayload.leaseEndDate)
        cleanAssetPayload.leaseEndDate = toDateOnly(cleanAssetPayload.leaseEndDate);
      // append organisation id of the user to the payload
      const assetPayloadBase = {
        ...cleanAssetPayload,
        organisationId: userProfile.org_id,
        assetFiles: newAsset.assetFiles,
      };

      if (noDuplicateSerial && newAsset.assetSerial) {
        const dup = await getAssetBySerial(
          newAsset.assetSerial,
          editingId,
        );
        if (dup) {
          setErrors({ assetSerial: 'Serial number already exists' });
          return;
        }
      }

      let savedAsset;
      if (editingId) {
        const updatePayload = {
          ...assetPayloadBase,
          updatedAt: new Date().toISOString(),
          updatedBy: userProfile.id,
        };
        if (updatePayload.assetParentId === "") { updatePayload.assetParentId = null; }
        // remove finance specific field accidentally merged into asset payload
        delete updatePayload.assetId;
        delete updatePayload.id;
        delete updatePayload.lifeInYears;
        savedAsset = await updateAsset(editingId, updatePayload);
        clearCache('groups');
      } else {
        const createPayload = {
          ...assetPayloadBase,
          createdAt: new Date().toISOString(),
          createdBy: userProfile.id,
        };
        if (createPayload.assetParentId === "") { createPayload.assetParentId = null; }
        delete createPayload.assetId;
        delete createPayload.id;
        delete createPayload.lifeInYears;
        savedAsset = await createAsset(createPayload);
        clearCache('groups');
      }

      const assetIdForTasks = editingId || savedAsset?.id;

      if (assetMeters.length) {
        const meterUpdates = assetMeters
          .map((meter) => {
            const resolvedEndOfLife = resolveMeterEndOfLifeValue(
              newAsset,
              meter,
              primaryEndOfLife,
            );
            if (resolvedEndOfLife == null) {
              return null;
            }
            return updateAssetMeter(meter.id, { endOfLife: resolvedEndOfLife });
          })
          .filter(Boolean);
        if (meterUpdates.length) {
          await Promise.all(meterUpdates);
        }
      }

      await tasksRef.current?.saveAiTasks(assetIdForTasks);

      const financeData = { ...financePayload, assetId: assetIdForTasks };
      if (Object.keys(financeData).length > 1) {
        if (assetFinancialId) {
          await updateAssetFinancial(assetFinancialId, financeData);
        } else {
          const created = await createAssetFinancial(financeData);
          setAssetFinancialId(created.id);
        }
      }

      closeModal();
      seteditingId(null);
      setSearchTerm('');
      await updateAssets();
      setNewAsset({});

    } catch (error) {
      console.error('Error saving asset:', error);
    }
  };

  return (
    <>
      {headerSearchContainer &&
        createPortal(
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ width: '200px' }}
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />,
          headerSearchContainer,
        )}
      <div className="card h-100 p-0 rounded-0">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex flex-wrap align-items-center gap-2 ms-3">
            {renderFilterControls()}
          </div>
          <div className="d-flex align-items-center gap-2 ms-auto me-8">
            <div className="dropdown me-2">
              <button
                className="btn btn-outline-secondary text-sm px-12 py-12 radius-4 dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                Export Data
              </button>
              <ul className="dropdown-menu p-12 border bg-base shadow">
                <li>
                  <button className="dropdown-item" onClick={handleExportExcel}>
                    Excel
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleExportCSV}>
                    CSV
                  </button>
                </li>
              </ul>
            </div>

            {userProfile?.privileges?.includes('assets:create') && (
              <div className="btn-group">
                <button
                  onClick={() => {
                    const cleared = Object.fromEntries(
                      Object.keys(newAsset).map((key) => [key, ''])
                    );
                    const defaultStartDate = new Date().toISOString().split('T')[0];
                    setNewAsset({
                      ...cleared,
                      plantStatusId: 6, // default to 'Operational'
                      assetStartDate: defaultStartDate,
                      costCenterId: defaultCostCentreId,
                      assetCriticalityId: 2,
                      assetFiles: [],
                      depnType: 'none',
                      leaseType: 'none',
                      leasePayments: 0,
                      leaseInterestRate: 0,
                      leaseStartDate: defaultStartDate,
                      residualValue: 0,
                      endValue: 0,
                    });
                    seteditingId(null);
                    setErrors({});
                    setShowWizard(true);
                  }}
                  className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
                >
                  <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                  Add Asset
                </button>
                <button
                  type="button"
                  className="btn btn-primary text-sm btn-sm dropdown-toggle dropdown-toggle-split"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <span className="visually-hidden">Toggle Dropdown</span>
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li className="my-6">
                    <button
                      className="dropdown-item py-8 d-flex align-items-center gap-2"
                      onClick={(e) => {
                        const toggleBtn = e.currentTarget
                          .closest('.btn-group')
                          ?.querySelector('.dropdown-toggle');
                        if (toggleBtn) {
                          Dropdown.getOrCreateInstance(toggleBtn).hide();
                        }
                        setShowBulkAddModal(true);
                      }}
                    >
                      <Icon
                        icon="mdi:clock-fast"
                        className="icon text-xl line-height-1"
                      />
                      Bulk Add Assets
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="card-body p-0">
          {viewMode === 'panel' ? (
            <AssetPanelView
              selectedAsset={selectedAsset}
              setSelectedAsset={setSelectedAsset}
              listContainerRef={listContainerRef}
              onEdit={handleEdit}
              onDelete={handleDelete}
              planthireEnabled={planthireEnabled}
              canEdit={canEditAsset}
              canDelete={canDeleteAsset}
              fetchAssetsForDepartment={fetchAssetsForDepartment}
              assetTreeRefreshKey={assetTreeRefreshKey}
              searchTerm={searchTerm}
            />
          ) : viewMode === 'map' ? (
            <AssetMapView
              assetLocationMarkers={assetLocationMarkers}
              totalAssetsCount={mapScopedAssets.length}
              googleTileUrl={googleTileUrl}
              loading={assetLocationLoading}
              error={assetLocationError}
              onOpenAsset={(assetId) => {
                const asset = mapScopedAssets.find((a) => Number(a.id) === Number(assetId));
                if (asset) handleEdit(asset);
              }}
            />
          ) : (
            <div
              className="paginated-table-container scroll-sm"
              ref={listContainerRef}
              style={{ height: 'calc(100vh - 216px)' }}
            >
              {viewMode === 'table' ? (
                <AssetDataTable
                  assets={assets}
                  useFunctionalLocation={useFunctionalLocation}
                  meteredAssetIds={meteredAssetIds}
                  planthireEnabled={planthireEnabled}
                  onView={handleView}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  sortField={sortField}
                  sortOrder={sortOrder}
                  onSort={handleSortChange}
                  canEditAsset={canEditAsset}
                />
              ) : (
                <div className="row g-3 p-3">
                  {assets.length === 0 && !isInitialAssetLoading && (
                    <div className="text-center w-100 py-4">No Assets Loaded</div>
                  )}
                  {assets.map((row) => {
                    const placeholder = `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`;
                    const imageUrl = row.assetImage
                      ? `${process.env.REACT_APP_SPACES_BASE}/${row.assetImage}`
                      : placeholder;
                    const statusColour = row.plantStatus?.colours?.bootstrapName || 'secondary';
                    const typeColour = getTypeColour(row.PlanningType?.typeDescription);
                    return (
                      <div key={row.id} className="col-lg-3 col-md-4">
                        <div className="card h-100 p-0 rounded-0 overflow-hidden">
                          <div className="card-body">
                            <div className="w-50 max-h-194-px radius-8 overflow-hidden">
                              <img
                                src={imageUrl}
                                alt="asset"
                                className="w-100 h-100 object-fit-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = placeholder;
                                }}
                              />
                            </div>
                            <div className="d-flex align-items-center justify-content-end">
                              <div
                                style={{
                                  position: 'relative',
                                  top: '-180px'
                                }}
                              >
                                {row.hireStatus === 1 ? (
                                  <div className="badge bg-success-focus text-success p-8">On Hire</div>
                                ) : row.hireStatus === 0 ? (
                                  <div className="badge bg-danger-focus text-danger p-8">Off Hire</div>
                                ) : <div className="badge bg-white text-white p-8">&nbsp;</div>}
                              </div>
                            </div>
                            <div className="mt-20">
                              <div className="d-flex align-items-center justify-content-between mb-16">
                                {row.plantStatus && (
                                  <span
                                    className={`px-20 py-6 bg-${statusColour}-focus text-${statusColour}-main rounded-pill text-sm`}
                                  >
                                    {row.plantStatus.status}
                                  </span>
                                )}
                                <span className="text-neutral-500 fw-medium">
                                  {row.assetDepartment?.name || ''}
                                </span>
                              </div>
                              <div className="d-flex align-items-center justify-content-between mb-8">
                                <span className="fw-bold">{row.assetNumber}</span>
                                {row.PlanningType && (
                                  <span className={`badge bg-${typeColour}`}>
                                    {row.PlanningType.typeDescription}
                                  </span>
                                )}
                              </div>
                              <div className="mb-8 text-sm">
                                {row.assetDescription}
                              </div>
                              <div className="text-sm">Serial No. {row.assetSerial}</div>
                              {(canEditAsset || canDeleteAsset) && (
                                <div className="d-flex justify-content-end gap-2 mt-3">
                                  {canEditAsset && (
                                    <button
                                      className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                                      onClick={() => handleEdit(row)}
                                    >
                                      <Icon icon="ic:baseline-edit" className="icon" />
                                      Edit
                                    </button>
                                  )}
                                  {canDeleteAsset && (
                                    <button
                                      className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                      onClick={() => handleDelete(row.id)}
                                    >
                                      <Icon icon="ic:outline-delete" className="icon" />
                                      Delete
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {isLoadingMoreAssets && (
                    <div className="text-center w-100 py-2">
                      <span className="spinner-border spinner-border-sm" />
                    </div>
                  )}
                </div>
              )}
              {viewMode === 'table' && isLoadingMoreAssets && (
                <div className="text-center w-100 py-2">
                  <span className="spinner-border spinner-border-sm" />
                </div>
              )}
            </div>
          )}
        </div>

        {showModal && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xxl">
              <div className="modal-content">
                <div className="modal-header">
                  {newAsset.assetImage && (
                    <img
                      src={`${process.env.REACT_APP_SPACES_BASE}/${newAsset.assetImage}`}
                      alt="User"
                      className="rounded-circle me-3"
                      style={{ height: '50px', width: '50px', objectFit: 'cover' }}
                    />
                  )}
                  <h5 className="modal-title">{editingId ? (`${newAsset?.assetNumber} - ${newAsset.assetDescription}`) : 'Add Asset'}</h5>
                  <div className="d-flex align-items-center ms-auto gap-2">
                    {planthireEnabled && newAsset.hireStatus !== -1 && (
                      <span
                        className={`badge ms-24 ${newAsset.hireStatus === 1
                          ? 'bg-success'
                          : 'bg-danger'
                          } text-sm`}
                      >
                        {newAsset.hireStatus === 1 ? 'On Hire' : 'Off Hire'}
                      </span>)}
                    {planningTypeName && (
                      <span className="badge bg-primary text-sm">{planningTypeName}</span>
                    )}
                    {assetTypeName && (
                      <span className="badge bg-secondary text-sm">{assetTypeName}</span>
                    )}
                    <button type="button" className="btn-close" onClick={closeModal} />
                  </div>
                </div>
                <div className="modal-body">
                  <ul className="nav nav-tabs mb-3" id="assetTab" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button
                        className="nav-link active"
                        id="general-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#general"
                        type="button"
                        role="tab"
                      >
                        General
                        {renderErrorDot(generalTabHasError)}
                      </button>
                    </li>
                    {userProfile?.privileges?.includes('asset-financial:view') && (
                      <li className="nav-item" role="presentation">
                        <button
                          className="nav-link"
                          id="finance-tab"
                          data-bs-toggle="tab"
                          data-bs-target="#finance"
                          type="button"
                          role="tab"
                        >
                          Finance
                          {renderErrorDot(financeTabHasError)}
                        </button>
                      </li>)}
                    {editingId && (
                      <>
                        {!isBasicType && (
                          <>
                            {userProfile?.privileges?.includes('tasks:view') && (
                              <li className="nav-item" role="presentation">
                                <button
                                  className="nav-link"
                                  id="tasks-tab"
                                  data-bs-toggle="tab"
                                  data-bs-target="#tasks"
                                  type="button"
                                  role="tab"
                                  onClick={() => setTasksLoaded(true)}
                                >
                                  Tasks
                                </button>
                              </li>)}
                            <li className="nav-item" role="presentation">
                              <button
                                className="nav-link"
                                id="utilisation-tab"
                                data-bs-toggle="tab"
                                data-bs-target="#utilisation"
                                type="button"
                                role="tab"
                              >
                                Meters &amp; Measurements
                              </button>
                            </li>
                            <li className="nav-item" role="presentation">
                              <button
                                className="nav-link"
                                id="meters-tab"
                                data-bs-toggle="tab"
                                data-bs-target="#meters"
                                type="button"
                                role="tab"
                              >
                                Meter Readings</button>
                            </li>
                          </>
                        )}
                        {rotablesEnabled && (
                          <li className="nav-item" role="presentation">
                            <button
                              className="nav-link"
                              id="rotables-tab"
                              data-bs-toggle="tab"
                              data-bs-target="#rotables"
                              type="button"
                              role="tab"
                              onClick={() => {
                                if (!rotablesTasksLoaded) {
                                  loadRotablesTasks(editingId);
                                  setRotablesTasksLoaded(true);
                                }
                              }}
                            >
                              Rotables
                            </button>
                          </li>
                        )}
                        {hasPageAccess('/work-orders') && userProfile?.privileges?.includes('work-orders:view') && (
                          <li className="nav-item" role="presentation">
                            <button
                              className="nav-link"
                              id="workorders-tab"
                              data-bs-toggle="tab"
                              data-bs-target="#workorders"
                              type="button"
                              role="tab"
                              onClick={() => setWorkOrdersLoaded(true)}
                            >
                              Work Orders
                            </button>
                          </li>)}
                        {userProfile?.privileges?.includes('forms:view') && (
                          <li className="nav-item" role="presentation">
                            <button
                              className="nav-link"
                              id="forms-tab"
                              data-bs-toggle="tab"
                              data-bs-target="#forms"
                              type="button"
                              role="tab"
                              onClick={() => {
                                if (!formsLoaded) loadAssetForms(editingId);
                                setFormsLoaded(true);
                              }}
                            >
                              Forms
                            </button>
                          </li>)}
                        <li className="nav-item" role="presentation">
                          <button
                            className="nav-link"
                            id="documents-tab"
                            data-bs-toggle="tab"
                            data-bs-target="#documents"
                            type="button"
                            role="tab"
                          >
                            Documents
                            {renderErrorDot(documentsTabHasError)}
                          </button>
                        </li>
                        {lccEnabled && (
                          <li className="nav-item" role="presentation">
                            <button
                              className="nav-link"
                              id="lcc-tab"
                              data-bs-toggle="tab"
                              data-bs-target="#lcc"
                              type="button"
                              role="tab"
                              onClick={() => setLifeCycleCostRequestKey((prev) => prev + 1)}
                            >
                              Life Cycle Cost
                            </button>
                          </li>
                        )}
                        {planthireEnabled && newAsset.hireStatus !== -1 && (
                          <li className="nav-item" role="presentation">
                            <button
                              className="nav-link"
                              id="planthire-tab"
                              data-bs-toggle="tab"
                              data-bs-target="#planthire"
                              type="button"
                              role="tab"
                            >
                              Plant Hire
                            </button>
                          </li>
                        )}
                      </>
                    )}
                  </ul>

                  <div className="tab-content" id="assetTabContent">
                    <div className="tab-pane fade show active" id="general" role="tabpanel" aria-labelledby="general-tab">
                      <div className="row g-3">
                        {assetGeneralFields
                          .filter((f) => useFunctionalLocation || f.field !== 'assetFloc')
                          .filter((f) => !editingId || f.field !== 'PlanningTypeId')
                          .filter((f) => !(isBasicType && f.field === 'assetCriticalityId'))
                          .filter((f) => !editingId || f.field !== 'lifeInYears')
                          .filter((f) => isBasicType || f.field !== 'generalSpacer2')
                          .map((f) => (
                            <React.Fragment key={f.field}>
                              {renderGeneralField(f)}
                              {f.field === 'assetEndDate' && assetMeters.find((m) => m.primaryMeter) && (
                                <div className="col-md-2">
                                  <label className="form-label">
                                    Useful Life in {
                                      meterList.find(
                                        (m) =>
                                          m.id === assetMeters.find((am) => am.primaryMeter)?.meterId,
                                      )?.measurementUnits?.unitName || ''
                                    }
                                  </label>
                                  <input
                                    type="number"
                                    step="1"
                                    className="form-control"
                                    value={primaryEndOfLife}
                                    onChange={(e) => setPrimaryEndOfLife(e.target.value.replace(/\D/g, ''))}
                                    onBlur={async () => {
                                      const primary = primaryMeter;
                                      if (!primary) return;

                                      const isValid = validatePrimaryEndOfLifeValue(primaryEndOfLife);
                                      if (!isValid) return;

                                      const parsedEndOfLife = parseNumber(primaryEndOfLife);
                                      const roundedEndOfLife =
                                        Number.isFinite(parsedEndOfLife) && parsedEndOfLife >= 0
                                          ? Math.round(parsedEndOfLife)
                                          : 0;
                                      await updateAssetMeter(primary.id, { endOfLife: roundedEndOfLife });
                                      await loadAssetMeters(editingId);
                                      if (
                                        primary.utilisation &&
                                        newAsset.assetStartDate &&
                                        roundedEndOfLife &&
                                        roundedEndOfLife - primaryStartReading > 0
                                      ) {
                                        const start = new Date(newAsset.assetStartDate);
                                        if (!isNaN(start.getTime())) {
                                          const years =
                                            (roundedEndOfLife - primaryStartReading) /
                                            Number(primary.utilisation);
                                          const endDate = new Date(
                                            start.getTime() + years * 365.25 * 24 * 60 * 60 * 1000,
                                          );
                                          const iso = endDate.toISOString();
                                          const dateOnly = iso.split('T')[0];
                                          setNewAsset((prev) => ({
                                            ...prev,
                                            assetEndDate: iso,
                                            assetDisposalDate: dateOnly,
                                            lifeInYears: years,
                                          }));
                                        }
                                      }
                                    }}
                                  />
                                  {primaryEndOfLifeErrors.primaryEndOfLife && (
                                    <div className="text-danger small">
                                      {primaryEndOfLifeErrors.primaryEndOfLife}
                                    </div>
                                  )}
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                        {assetInfoFields
                          .filter((f) => editingId || f.field !== 'assetDisposed')
                          .filter((f) =>
                            showExtraAssetFields ||
                            !['assetCode', 'assetAltNumber', 'planningGroupId'].includes(f.field)
                          )
                          .map(renderInfoFields)}
                        {assetCategoryField
                          .filter((f) =>
                            showAssetCategory ||
                            !['assetCategoryId'].includes(f.field)
                          )
                          .map(renderCategoryField)}
                        {newAsset.assetDisposed ? (
                          <div className="col-md-4">
                            <label className="form-label">Disposal Date</label>
                            <input
                              type="date"
                              className="form-control"
                              name="assetDisposalDate"
                              value={newAsset.assetDisposalDate?.slice(0, 10) || ''}
                              onChange={handleInputChange}
                              readOnly={formReadOnly}
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="tab-pane fade" id="finance" role="tabpanel" aria-labelledby="finance-tab" style={{ minHeight: '400px' }}>
                      <div className="row g-3">

                        {assetFinanceFields.map(renderFinanceFields)}

                        <div className="col-md-12">
                          <span className="text-lg fw-semibold mt-24 mb-2">Depreciation Chart</span>
                          {depreciationChart.hasData && (
                            <ReactApexChart
                              options={depreciationChart.options}
                              series={depreciationChart.series}
                              type="line"
                              height={260}
                            />

                          )}
                        </div>
                      </div>
                    </div>
                    {rotablesEnabled && (
                      <div className="tab-pane fade" id="rotables" role="tabpanel" aria-labelledby="rotables-tab">
                        {rotablesTasksLoading ? (
                          <div className="text-center py-4">
                            <span className="spinner-border spinner-border-sm" />
                          </div>
                        ) : (
                          <>
                            {rotableTasks.length ? (
                              <div className="table-responsive">
                                <table className="table bordered-table dataTable mb-0 w-100">
                                  <thead>
                                    <tr>
                                      <th>Task Code</th>
                                      <th>Task Name</th>
                                      <th>Serial Number</th>
                                      <th>Part Number</th>
                                      <th>Component Type</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rotableTasks.map((task) => {
                                      const rotable = task?.rotables || task?.rotable;
                                      return (
                                        <tr key={task.id}>
                                          <td>{task?.code?.code || task?.taskCode?.code || ''}</td>
                                          <td>{task?.code?.name || task?.taskCode?.name || ''}</td>
                                          <td>{rotable?.serialNumber || ''}</td>
                                          <td>{rotable?.part?.partNumber || ''}</td>
                                          <td>{rotable?.componentType?.name || ''}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="text-muted">
                                {editingId
                                  ? 'No rotable tasks found for this asset.'
                                  : 'Save the asset to view rotable tasks.'}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    {editingId && (
                      <>
                        {!isBasicType && (
                          <>
                            <div className="tab-pane fade" id="tasks" role="tabpanel" aria-labelledby="tasks-tab">
                              {tasksLoaded && (
                                <AssetTasks
                                  ref={tasksRef}
                                  key={editingId}
                                  assetId={editingId}
                                  asset={newAsset}
                                />
                              )}
                            </div>
                            <div className="tab-pane fade" id="utilisation" role="tabpanel" aria-labelledby="utilisation-tab">

                              <div className="row g-3">
                                <div className="col-md-10">
                                  <label className="form-label">Assigned Meters</label>
                                  <div className="table-responsive">
                                    <table className="table bordered-table dataTable mb-3 w-100">
                                      <thead>
                                        <tr>
                                          <th>Name</th>
                                          <th>Start Reading</th>
                                          <th>Annual Utilisation</th>
                                          <th>Primary</th>
                                          <th>Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {assetMeters.map((am) => (
                                          <tr key={am.id}>
                                            <td>{meterList.find((m) => m.id === am.meterId)?.name || `Meter ${am.meterId}`}</td>
                                            <td>{Math.round(am.startReading)}</td>
                                            <td>{Math.round(am.utilisation)}</td>
                                            <td>
                                              <div className="form-switch switch-primary d-flex align-items-center">
                                                <input className="form-check-input" type="checkbox" checked={Boolean(am.primaryMeter)} disabled />
                                              </div>
                                            </td>
                                            <td>
                                              <button
                                                className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                                onClick={() => handleEditMeter(am)}
                                              >
                                                <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                                <div className="col-md-2">
                                  <label className="form-label">&nbsp;</label>
                                  <div className="text-left">
                                    <button
                                      type="button"
                                      className="btn btn-primary mb-2"
                                      onClick={() => {
                                        setNewMeterId(null);
                                        setNewPrimaryMeter(assetMeters.length === 0);
                                        setShowAddMeterModal(true);
                                      }}
                                    >
                                      <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                                      Add Meter
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="tab-pane fade" id="meters" role="tabpanel" aria-labelledby="meters-tab">
                              <AssetMeterReadings assetId={editingId} />
                            </div>
                          </>
                        )}
                        <div
                          className="tab-pane fade"
                          id="workorders"
                          role="tabpanel"
                          aria-labelledby="workorders-tab"
                        >
                          {workOrdersLoaded && (
                            <>
                              <WorkOrderMaintenanceIntensity assetId={editingId} />
                              <AssetWorkOrders assetId={editingId} />
                            </>
                          )}
                        </div>
                        <div
                          className="tab-pane fade"
                          id="forms"
                          role="tabpanel"
                          aria-labelledby="forms-tab"
                        >
                          {formsLoaded && (
                            <AssetFormsDataTable
                              forms={assetForms}
                              loading={formsLoading}
                              onView={(row) => setViewFormId(row.id)}
                            />
                          )}
                        </div>
                        <div
                          className="tab-pane fade"
                          id="documents"
                          role="tabpanel"
                          aria-labelledby="documents-tab"
                        >
                          <div className="row g-3">{assetDocumentFields.map(renderDocumentFields)}</div>
                          {Array.isArray(newAsset.assetFiles) && newAsset.assetFiles.length > 0 && (
                            <DocumentDataTable files={newAsset.assetFiles} onDelete={handleFileDelete} />
                          )}
                        </div>
                        {lccEnabled && (
                          <div
                            className="tab-pane fade"
                            id="lcc"
                            role="tabpanel"
                            aria-labelledby="lcc-tab"
                          >
                            <AssetLifeCycleCostTab
                              assetId={editingId}
                              requestKey={lifeCycleCostRequestKey}
                            />
                          </div>
                        )}
                        {planthireEnabled && newAsset.hireStatus !== -1 && (
                          <div
                            className="tab-pane fade"
                            id="planthire"
                            role="tabpanel"
                            aria-labelledby="planthire-tab"
                          >
                            <div className="row g-3 align-items-center">
                              <div className="col-md-2">
                                <label className="form-label mb-8">Hire Status</label>
                                <div className="form-switch switch-primary d-flex align-items-center">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={newAsset.hireStatus === 1}
                                    onChange={(e) =>
                                      setNewAsset((prev) => ({
                                        ...prev,
                                        hireStatus: e.target.checked ? 1 : 0,
                                      }))
                                    }
                                    disabled={formReadOnly}
                                  />
                                  <span
                                    className={`badge ms-24 ${newAsset.hireStatus === 1
                                      ? 'bg-success-focus text-success'
                                      : 'bg-danger-focus text-danger'
                                      } text-md`}
                                  >
                                    {newAsset.hireStatus === 1 ? 'On Hire' : 'Off Hire'}
                                  </span>
                                </div>
                              </div>
                            
                              <div className="col-md-3">
                                <label className="form-label">Customer</label>
                                <select
                                  className="form-select"
                                  value={getSelectValue(newAsset.customerId ?? newAsset.customer?.id)}
                                  onChange={(e) => handleCustomerSelect(e.target.value)}
                                  disabled={formReadOnly}
                                >
                                  <option value="">Select Customer</option>
                                  {customerList.map((customer) => (
                                    <option key={customer.id} value={customer.id}>
                                      {customer.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="col-md-3">
                                <label className="form-label">Project</label>
                                <select
                                  className="form-select"
                                  value={getSelectValue(newAsset.projectId ?? newAsset.project?.id)}
                                  onChange={(e) => handleProjectSelect(e.target.value)}
                                  disabled={formReadOnly || !selectedCustomerId}
                                >
                                  <option value="">Select Project</option>
                                  {projectList.map((project) => (
                                    <option key={project.id} value={project.id}>
                                      {project.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              {newAsset.hireAssetSwitch && Number(newAsset.projectId ?? newAsset.project?.id) > 0 && (
                                <div className="col-md-3">
                                  <label className="form-label">Plant Rate</label>
                                  <select
                                    className="form-select"
                                    value={getSelectValue(newAsset.plantRateId)}
                                    onChange={(e) => setNewAsset((prev) => ({ ...prev, plantRateId: e.target.value ? Number(e.target.value) : null }))}
                                    disabled={formReadOnly}
                                  >
                                    <option value="">Select Plant Rate</option>
                                    {projectPlantRates.map((rate) => (
                                      <option key={rate.id} value={rate.id}>{rate.name}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                            {!!newAsset.plantRateId && (
                              <>
                                <h6 className="mt-24 mb-12 ms-8 text-xl">Plant Rate</h6>
                                  <div className="row g-3 mb-16">
                                    <div className="col-md-6">
                                  <table className="table mb-0 bordered-table">
                                    <thead>
                                      <tr>
                                        <th>Category</th>
                                        <th className="text-end" style={{width: "100px"}}>Usage Rate</th>
                                        <th>Expense Code</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {selectedPlantRateTableRows.rows.map((row) => (
                                        <tr key={`${row.label}-${row.expense}`}>
                                          <td>{row.label}</td><td className="text-end">{Number(row.value || 0).toFixed(2)}</td><td>{row.expense}</td>
                                        </tr>
                                      ))}
                                      <tr><td><strong>Total</strong></td><td className="text-end"><strong>{selectedPlantRateTableRows.total.toFixed(2)}</strong></td><td /></tr>
                                    </tbody>
                                  </table>
                                  </div>
                                  </div>
                              </>

                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={closeModal}>Close</button>
                  {!formReadOnly && (
                    <>
                      <button className="btn btn-primary" onClick={handleSave}>Save</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {showWizard && (
          <AddAssetWizard
            show={showWizard}
            onClose={() => setShowWizard(false)}
            onSaved={updateAssets}
          />
        )}
        <AssetBulkAddModal
          show={showBulkAddModal}
          onClose={() => setShowBulkAddModal(false)}
          onSaved={updateAssets}
          assetTypes={assetTypes}
          planningTypes={PlanningTypes}
        />
        {showAddMeterModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add Meter</h5>
                  <button
                    className="btn-close"
                    onClick={() => {
                      setShowAddMeterModal(false);
                      setNewPrimaryMeter(false);
                      resetAddMeterErrors();
                    }}
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Meter *</label>
                    <select
                      className="form-select"
                      value={newMeterId || ''}
                      onChange={(e) => setNewMeterId(Number(e.target.value))}
                    >
                      <option value="" disabled>Select Meter</option>
                      {availableMeters.map((m) => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                    {addMeterErrors.meterId && (
                      <div className="text-danger small">{addMeterErrors.meterId}</div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Start Reading</label>
                    <input
                      type="number"
                      step="1"
                      className="form-control"
                      value={newStartReading}
                      onChange={(e) => setNewStartReading(e.target.value.replace(/\D/g, ''))}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Utilisation per Year</label>
                    <input
                      type="number"
                      step="1"
                      className="form-control"
                      value={newUtilisation}
                      onChange={(e) => setNewUtilisation(e.target.value)}
                    />
                    {addMeterErrors.utilisation && (
                      <div className="text-danger small">{addMeterErrors.utilisation}</div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Primary Meter</label>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mt-10">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={newPrimaryMeter}
                        disabled
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAddMeterModal(false);
                      setNewPrimaryMeter(false);
                      resetAddMeterErrors();
                    }}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleSaveMeter}>Save</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showEditMeterModal && editingMeter && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Meter</h5>
                  <button className="btn-close" onClick={() => setShowEditMeterModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Meter</label>
                    <input
                      className="form-control"
                      value={meterList.find((m) => m.id === editingMeter.meterId)?.name || editingMeter.meterId}
                      readOnly
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Start Reading</label>
                    <input
                      type="number"
                      step="1"
                      className="form-control"
                      value={editingMeter.startReading || ''}
                      onChange={(e) =>
                        setEditingMeter({
                          ...editingMeter,
                          startReading: e.target.value.replace(/\D/g, ''),
                        })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Utilisation per Year</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editingMeter.utilisation || ''}
                      onChange={(e) => setEditingMeter({ ...editingMeter, utilisation: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Primary Meter</label>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mt-10">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={Boolean(editingMeter.primaryMeter)}
                        onChange={(e) =>
                          setEditingMeter({
                            ...editingMeter,
                            primaryMeter: e.target.checked ? 1 : 0,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowEditMeterModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleUpdateMeter}>Save</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {viewFormId && (
          <FormViewModal
            workOrderFormId={viewFormId}
            onClose={() => setViewFormId(null)}
            readOnly
          />
        )}
      </div>
    </>
  );
};

export default AssetListLayer;
